/**
 * Free-tier (vendor) plan access rules.
 *
 * A vendor org signs up through a bid invite and lands on `vendor_free`
 * (see 20260904220000_vendor_free_billing_tier.sql). Those accounts get the
 * dashboard, work orders, and the vendor portal — everything else in the app
 * renders the upgrade page instead of its content.
 *
 * Routing stays completely normal: the URL, the sidebar, and the back button
 * all behave as they would on a paid plan. Only the page body changes, so the
 * blocked pages read as "not on your plan yet" rather than "broken".
 *
 * Moving one vendor onto a plan is a single write:
 *   update public.organizations set billing_tier = 'paid' where id = '<org>';
 */

/** billing_tier value that means "free vendor account, paid features locked" */
export const VENDOR_FREE_TIER = 'vendor_free'

/**
 * Route prefixes a vendor_free org can open. A prefix matches the path
 * itself and anything nested under it, so '/work-orders' covers the list,
 * '/work-orders/:id', and '/work-orders/new'.
 *
 * /change-password isn't a feature — it's the forced password-change screen,
 * and locking it would trap a user who has to set a password before anything
 * else works.
 */
export const VENDOR_FREE_ALLOWED_PREFIXES = [
  '/dashboard',
  '/work-orders',
  '/portal/vendor',
  '/change-password',
] as const

/** True when a vendor_free org is allowed to see this path's real content. */
export function isPathAllowedOnVendorFree(pathname: string): boolean {
  if (pathname === '/') return true
  return VENDOR_FREE_ALLOWED_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  )
}
